import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import moment from 'moment'
import { useAtomValue, useSetAtom } from 'jotai'
import useToken from '../../helpers/useTokens'
import { branchListAtom, CargoRoleRightsAtom } from '../../common/jotaiState/commonStateAtoms'
import FilterSection from './FilterSection'
import CapacitySummary from './CapacitySummary'
import TripTable from './TripTable'
import LoadEditorModal from './LoadEditorModal'
import ViewLoadModal from './ViewLoadModal'
import { formatTripDateTime, getTripSummary, parseTripMoment } from './helpers'
import { lrHeaders, planHeaders, tripTableColumns } from './constants'
import { GetRequest, PostRequest } from '../../common/api'
import { urls } from '../../common/constants'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { safeAdd } from '../../common/mathHelper'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { Typography } from '@mui/material'
import { exportToExcel } from '../../common/hook'
import Modal from '../../common/modal'
import { callAddFontRobotoBold } from '../../common/font/Roboto-bold'
import { callAddFontRobotoMedium } from '../../common/font/Roboto-Medium-normal'
import jsPDF from 'jspdf'
import { makeTable } from '../Reports/analysisReport/pdfAutotable'
import autoTable from 'jspdf-autotable'
import { lrDetailPdfFooter } from '../Reports/BookingReports/BookingConstants'
import { ChildCompanyListData, fetchChildCompanyListData } from '../../common/jotaiState/reportStateAtom'

const reportName = 'Dispatch Load Plan'

export const PlanDisaptch = () => {
  const setLoader = useSetAtom(globalLoaderAtom)
  const setAlert = useSetAtom(alertAtom)
  const { loginData } = useToken()
  const [selectCompany, setSelectedCompany] = useState({})
  const { intCompanyID, intBranchID, strCompanyName, intUserID, intBranchCityID, intCityID, IsAdmin, IsMTPLAdmin, IsBranchAdmin } = loginData
  const roleRights = useAtomValue(CargoRoleRightsAtom)
  const RoleRights = roleRights?.Table1?.[0] || {}
  const { AllowViewAllBranchReport, AllowViewAllBranchOfCityReport, AllowBranchInchargeRights } = RoleRights
  const canViewAllBranches = !!(
    AllowViewAllBranchReport ||
    AllowBranchInchargeRights ||
    IsAdmin ||
    IsMTPLAdmin ||
    IsBranchAdmin
  )
  const branchDisabled = !canViewAllBranches && AllowViewAllBranchOfCityReport == 0
  const [branchList, setBranchList] = useState([])
  const [branch, setBranch] = useState(null)
  const [tripDate, setTripDate] = useState(moment())
  const [tripList, setTripList] = useState([])
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [bookingList, setBookingList] = useState([])
  const [tripDetails, setTripDetails] = useState(null)
  const [loadedWeight, setLoadedWeight] = useState(0)
  const [maximumWeight, setMaximumWeight] = useState(0)
  const availableWeight = maximumWeight - loadedWeight
  const [selectedWeight, setSelectedWeight] = useState(0)
  const [activeModal, setActiveModal] = useState(null)
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const ChildCompanyList = useAtomValue(ChildCompanyListData)
  const fetchChildCompanyList = useSetAtom(fetchChildCompanyListData)

  const summary = useMemo(() => getTripSummary(tripList), [tripList])
  useEffect(() => {
    if (intCompanyID) {
      const companyList = [{ CompanyID: intCompanyID, CompanyName: strCompanyName }]
      setSelectedCompany(companyList[0])
      getBranchList(companyList[0])
      const body = {
        intCompanyID: intCompanyID,
        UserID: intUserID,
      }
      fetchChildCompanyList(body)
    }
  }, [intCompanyID])

  useEffect(() => {
    if (selectCompany?.CompanyID) {
      getBranchList(selectCompany)
    }
  }, [canViewAllBranches, AllowViewAllBranchOfCityReport])

  const getBranchList = (company) => {
    const obj = {
      intUserID: 0,
      intCompanyID: company?.CompanyID || intCompanyID,
      intBranchID: 0,
      strBranchName: '',
      strBranchCode: '',
      intStateID: 0,
      intCityID: 0,
      intIsActive: 1,
    }
    PostRequest(urls.getBranchConfigOrEwbillinfo, obj).then((res) => {
      if (res.status === 'Success') {
        let filteredBranches = res?.Data?.Table?.filter((item) => item.BranchTypeID === 2 || item.BranchTypeID === 3) || []
        if (!canViewAllBranches) {
          if (AllowViewAllBranchOfCityReport == 1) {
            const cityId = intBranchCityID || intCityID
            filteredBranches = filteredBranches.filter((item) => item.CityID === cityId)
          } else {
            filteredBranches = filteredBranches.filter((item) => item.BranchID === intBranchID)
          }
        }
        const loginBranch = filteredBranches.find((item) => item.BranchID === intBranchID)
        setBranch(loginBranch || filteredBranches[0] || null)
        setBranchList(filteredBranches)
      }
    })
  }
  const handleCompanyChange = (company) => {
    setSelectedCompany(company)
    getBranchList(company)
  }

  const handleLoadTrips = async () => {
    if (!branch?.BranchName) {
      setAlert({
        show: true,
        msg: 'Please select a branch',
      })
      return
    }
    const payload = {
      intCompanyID: selectCompany?.CompanyID || intCompanyID,
      BranchID: branch?.BranchID || 0,
      TripDate: tripDate.format('YYYY-MM-DD'),
    }
    setLoader(true)
    GetRequest(urls.getLoadTrips, payload)
      .then((res) => {
        if (res.status !== 'Success') throw new Error(res.message)
        if (!res.Data.Table?.length) throw new Error('No Data found')
        const list = (res.Data.Table || []).map((item) => ({
          ...item,
          TripDateView: formatTripDateTime(item),
        }))
        list.sort((a, b) => {
          const da = parseTripMoment(a)
          const db = parseTripMoment(b)
          if (!da.isValid() && !db.isValid()) return 0
          if (!da.isValid()) return 1
          if (!db.isValid()) return -1
          return da.valueOf() - db.valueOf()
        })
        setTripList(list)
        setSelectedTrip(null)
      })
      .catch((error) => {
        setAlert({
          show: true,
          msg: error.message || 'Failed to load trips',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const handleTripSelection = (_, row) => {
    setSelectedTrip(row)
  }

  const openAddModal = () => {
    setActiveModal('add')
    onLoadPendency(selectedTrip)
  }

  const openManageModal = () => {
    setActiveModal('manage')
    onLoadPendency(selectedTrip)
  }
  const loadTripDetails = async (row, isView = false) => {
    try {
      setLoader(true)

      const payload = {
        intCompanyID: selectCompany?.CompanyID || intCompanyID,
        TripID: row.TripID,
        TripDate: row.TripDate,
        BranchID: branch?.BranchID || 0,
      }

      const res = await GetRequest(urls.getLoadTripBookingDetails, payload)

      if (res.status !== 'Success') throw new Error(res.message)

      if (!res.Data.Table1?.length) throw new Error('No LRs available in this trip')

      const bookings = res.Data.Table1.map((item) => ({
        ...item,
        checked: !!item.AssignmentID,
        SinceDateShow: moment(item.SinceDate).format('DD MMM YYYY'),
        dataType: 'entry',
      }))
      const loadedWeight = bookings.reduce((acc, item) => {
        if (item.AssignmentID) {
          return safeAdd(acc, item.Weight)
        }
        return acc
      }, 0)
      setLoadedWeight(loadedWeight)
      setSelectedWeight(loadedWeight)
      const trip = {
        ...res.Data.Table[0],
        tripRoute: `${res.Data.Table?.[0]?.FromCity} - ${res.Data.Table?.[0]?.ToCity}: ${res.Data.Table?.[0]?.MiddleCities}`,
        TripDateView: formatTripDateTime(res.Data.Table?.[0]),
        dataType: 'entry',
      }

      setMaximumWeight(trip.TotalCapacity || 0)
      setBookingList(bookings)
      setTripDetails(trip)

      if (isView) setActiveModal('view')
    } catch (err) {
      setAlert({
        show: true,
        msg: err.message,
        nextId: '',
        close: () => {
          setAlert({})
          closeModal()
        },
      })
    } finally {
      setLoader(false)
    }
  }
  const onLoadPendency = (row) => {
    loadTripDetails(row)
  }

  const openViewModal = (row) => {
    setSelectedTrip(row)
    loadTripDetails(row, true)
  }
  const onSelectionChange = (idOrIds, checked) => {
    if (!bookingList.length) return
    const ids = new Set(Array.isArray(idOrIds) ? idOrIds : [idOrIds])
    const updatedBookingList = bookingList.map((item) => {
      if (!ids.has(item.BookingID)) return item
      return {
        ...item,
        checked: checked !== undefined ? checked : !item.checked,
      }
    })
    const selectedWeight = updatedBookingList.reduce((acc, item) => {
      if (item.checked) {
        return safeAdd(acc, item.Weight)
      }
      return acc
    }, 0)

    setSelectedWeight(selectedWeight)
    setBookingList(updatedBookingList)
  }

  const closeModal = () => {
    setActiveModal(null)
    setBookingList([])
    setTripDetails(null)
  }
  const savePlan = () => {
    const { assigned, city, weights, deleted, units } = bookingList.reduce(
      (acc, item) => {
        if (item.checked && !item.AssignmentID) {
          acc.assigned.push(item.BookingID)
          acc.city.push(`${item.FromCityID}-${item.DestCityID}`)
          acc.weights.push(item.Weight)
          acc.units += item.Items
        } else if (!item.checked && item.AssignmentID) {
          acc.deleted.push(item.AssignmentID)
        }
        return acc
      },
      { assigned: [], city: [], weights: [], deleted: [], units: 0 }
    )
    if (deleted.length === 0 && assigned.length === 0) {
      return
    }
    const payload = {
      CompanyID: selectCompany?.CompanyID || intCompanyID,
      TripID: selectedTrip.TripID,
      ChartDate: selectedTrip.TripDate,
      VehicleID: selectedTrip.VehicleID,
      AssignBookings: assigned.join('^'),
      DeleteAssignedBookings: deleted.join('^'),
      CityIDs: city.join('^'),
      weights: weights.join('^'),
      LoadedWeight: selectedWeight,
      BranchID: branch?.BranchID || 0,
    }
    setLoader(true)
    PostRequest(urls.assignDispatchLoadPlan, payload)
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res.ErrMessage || 'Unable to save load plan.')
        }
        if (res.status === 'Success') {
          setAlert({
            show: true,
            msg: (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 1,
                  px: 2,
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    fontSize: '40px !important',
                    color: '#4CAF50 !important',
                    display: 'block',
                    mx: 'auto',
                    mb: 2,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Load Updated Successfully!
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {selectedTrip.VehicleNo} | Trip ID: {selectedTrip.TripID}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                Trip Date-Time: {formatTripDateTime(selectedTrip)}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Trip Name: {selectedTrip.TripName}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  Total Units: {units} | Total Weight: {selectedWeight} KG
                </Typography>
              </Box>
            ),
            success: true,
            close: () => {
              setAlert({})
              setActiveModal(null)
            },
          })
        }
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message || 'Unable to save load plan.',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }
  const tripHeaderColumns = tripTableColumns.map((col) => {
    return { header: col?.headerName, dataKey: col.field, field: col.field, xlCol: col.xlCol }
  })
  const lrHeaderColumns = lrHeaders.filter((column) => column.field !== 'select').map((col) => {
    return { header: col?.header, dataKey: col.field, field: col.field, xlCol: col.xlCol }
  })
  const onExcel = () => {
    exportToExcel({
      fileName: `Dispatch Load Plan ${moment().format('DD-MM-YYYY HH:mm:ss')}.xlsx`,
      sheetName: 'Dispatch Load Plan',
      headersList: [tripHeaderColumns, lrHeaderColumns],
      dataList: [[tripDetails], bookingList.filter((item) => item.AssignmentID)],
    })
  }
  const onPrint = () => {
    setPrintModalOpen(true)
    jsPDF.API.events.push(['addFonts', callAddFontRobotoBold])
    jsPDF.API.events.push(['addFonts', callAddFontRobotoMedium])
    const doc = new jsPDF({ orientation: 'p', format: [1239, 1754], unit: 'px', compress: true })

    doc.setProperties({
      title: `${reportName}.pdf`,
      subject: `${reportName} Report`,
      author: 'Maven Tech Labs Pvt. Ltd.',
      keywords: 'maven tech, Cargo system, Receive',
      creator: 'MTLPL',
    })
    var pdfFontSize = 1
    const customFunction = (data, doc) => {
      doc.setTextColor(pdfFontSize * 40)
      doc.addImage('/images/logo-66.png', 'JPEG', 30, 50, 50, 50)
      doc.setFontSize(pdfFontSize * 20).setFont('Roboto', 'bold')
      doc.setTextColor(16, 79, 162)
      doc.text('Cargo System | Dispatch Load Plan Report', 30 + 60, 80)
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(pdfFontSize * 20).setFont('Roboto', 'normal')
      doc.text(`Company Name :${loginData.strCompanyName}`, doc.internal.pageSize.width - data.settings.margin.left, 80, 'right')
    }
    const customFunction2 = (data, doc) => {

    }
    makeTable(doc, autoTable, tripHeaderColumns, [tripDetails], 1, reportName, loginData, 'everyPage', customFunction, 120,false)
    makeTable(doc, autoTable, lrHeaderColumns, bookingList.filter((item) => item.AssignmentID), 1, reportName, loginData, 'everyPage', customFunction2, 200, false,true)

    doc.setFont('Roboto')
    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Footer Font
      doc.setFont("Roboto", "bold");
      doc.setFontSize(pdfFontSize * 14);

      // Left
      doc.text(
        `Report By : ${loginData.strUserName} [ ${loginData.strBranchName} ]`,
        30,
        pageHeight - 5
      );

      // Center
      const reportTime = `Report Time : ${moment().format("DD MMM YYYY, ddd, HH:mm")}`;
      doc.text(reportTime, pageWidth / 2, pageHeight - 5, {
        align: "center",
      });

      // Right
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - 20,
        pageHeight - 5,
        {
          align: "right",
        }
      );
    }
    setTimeout(() => {
      const pdfUrl = doc.output('bloburl')
      document.getElementById('dispatchLoadPlanOutput').data = pdfUrl
    }, 2000)
  }
  return (
    <Box
      sx={{
        background: '#f7f9fc',
        minHeight: 'calc(100vh - 70px)',
        p: 2,
      }}
    >
      <Box sx={{ p: 0, display: 'flex', gap: 2 }}>
        <FilterSection
          selectCompany={selectCompany}
          setSelectedCompany={handleCompanyChange}
          branch={branch}
          tripDate={tripDate}
          branchList={branchList}
          branchDisabled={branchDisabled}
          onBranchChange={setBranch}
          onDateChange={setTripDate}
          onLoadTrips={handleLoadTrips}
        />

        {tripList.length ? <CapacitySummary summary={summary} /> : <></>}
      </Box>
      {tripList.length ?

        <TripTable
          headers={planHeaders}
          rows={tripList}
          selectedTrip={selectedTrip}
          onSelectTrip={handleTripSelection}
          onView={openViewModal}
          onAdd={openAddModal}
          onManage={openManageModal}
        />
        : <></>}
      {activeModal === 'add' || activeModal === 'manage' ? (
        <LoadEditorModal
          open={true}
          mode={activeModal}
          trip={selectedTrip}
          onClose={closeModal}
          onLoadPendency={onLoadPendency}
          onSelectionChange={onSelectionChange}
          lrRows={bookingList}
          onSave={savePlan}
          loadedWeight={loadedWeight}
          maximumWeight={maximumWeight}
          availableWeight={availableWeight}
          selectedWeight={selectedWeight}
        />
      ) : (
        <></>
      )}

      {activeModal === 'view' ? <ViewLoadModal open={true} trip={tripDetails} onExcel={onExcel} onPrint={onPrint} lrRows={bookingList.filter((item) => item.AssignmentID)} onClose={closeModal} /> : <></>}
      {printModalOpen ? <Modal width="95%"
        height="85%"
        headTxt="Print Dispatch Load Plan"
        updateModal={setPrintModalOpen}>
        <object
          id="dispatchLoadPlanOutput"
          type="application/pdf"
          style={{
            width: "100%",
            height: "calc(100% - 53px)",
          }}
          title="MTLPL -Cargo System - Dispatch Load Plan"
          aria-label="MTLPL -Cargo System - Dispatch Load Plan"
        ></object>
      </Modal> : <></>}


    </Box>
  )
}
